import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RealtimeDBService } from '../firebase/unifiedDataService';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { ViewLayout, LoadingScreen, ErrorScreen } from '../components/ui';
import LeaderboardTab from '../components/results/LeaderboardTab';
import QuestionStatsTab from '../components/results/QuestionStatsTab';
import ExportTab from '../components/results/ExportTab';
import { Logger } from '../utils/logger';

const log = Logger.create('ResultsView');

/**
 * Phase 6: Results & Analytics View
 * 
 * Comprehensive results interface for completed quizzes
 * Features:
 * - Tabbed interface (Leaderboard, Question Stats, Export)
 * - Participant rankings with scores
 * - Per-question analysis (correct/incorrect rates)
 * - Export to CSV, PDF, JSON
 * - Instance comparison (bonus)
 */
function ResultsView() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const { user } = useUnifiedAuth();

  const [activeTab, setActiveTab] = useState('leaderboard');
  const [instance, setInstance] = useState(null);
  const [participants, setParticipants] = useState({});
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load instance data
  useEffect(() => {
    if (!user || !instanceId) return;

    const loadInstanceData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load instance using RealtimeDBService
        const instanceData = await RealtimeDBService.get(`quiz_instances/${user.uid}/${instanceId}`);

        if (!instanceData) {
          setError('Quiz instance not found');
          setLoading(false);
          return;
        }

        setInstance({ id: instanceId, ...instanceData });

        // Load participants from quiz_instances (not sessions!)
        const participantsData = await RealtimeDBService.get(`quiz_instances/${user.uid}/${instanceId}/participants`);

        if (participantsData) {
          setParticipants(participantsData);
        }

        // Load answers from quiz_instances (not sessions!)
        const answersData = await RealtimeDBService.get(`quiz_instances/${user.uid}/${instanceId}/answers`);

        if (answersData) {
          setAnswers(answersData);
        }

        setLoading(false);
      } catch (err) {
        log.error('Error loading instance data:', err);
        setError('Error loading quiz data');
        setLoading(false);
      }
    };

    loadInstanceData();
  }, [user, instanceId]);

  // Calculate participant scores
  // Answers structure is: answers[questionIndex][participantId] = { selectedIndex/selectedIndices, ... }
  const calculateScores = () => {
    if (!instance || !instance.questions) return [];

    const scores = [];
    const questions = instance.questions;

    Object.entries(participants).forEach(([participantId, participant]) => {
      let correctCount = 0;
      let totalAnswered = 0;

      questions.forEach((question, questionIndex) => {
        // Get answer for this question and participant
        const questionAnswers = answers[questionIndex] || {};
        const answer = questionAnswers[participantId];
        
        if (answer !== undefined && answer !== null) {
          totalAnswered++;
          
          // Get the selected index (support both old and new format)
          const selectedIndex = answer.selectedIndex !== undefined ? answer.selectedIndex : answer;
          const selectedIndices = answer.selectedIndices || (selectedIndex !== null ? [selectedIndex] : []);
          
          // Get correct answer indices
          const correctIndices = question.correctAnswerIndices || 
            (question.correctAnswer !== undefined ? [question.correctAnswer] : [0]);
          
          // Check if answer is correct
          if (selectedIndices.length === correctIndices.length &&
              correctIndices.every(idx => selectedIndices.includes(idx))) {
            correctCount++;
          }
        }
      });

      // Use participant's stored totalScore if available (from gamification)
      // totalScore > 0 means gamification was enabled and points were tracked
      const storedScore = participant.totalScore || 0;
      const isGamificationEnabled = storedScore > 0;
      
      // For non-gamification quizzes, calculate percentage
      const calculatedPercentage = questions.length > 0 ? (correctCount / questions.length) * 100 : 0;

      scores.push({
        participantId,
        name: participant.name || 'Anonymous',
        correctCount,
        totalAnswered,
        totalQuestions: questions.length,
        // Store actual points if gamification, otherwise store percentage
        score: isGamificationEnabled ? storedScore : calculatedPercentage.toFixed(1),
        isGamification: isGamificationEnabled, // Flag to know how to display
        accuracy: questions.length > 0 ? ((correctCount / questions.length) * 100).toFixed(1) : '0.0',
        joinedAt: participant.joinedAt || 0
      });
    });

    // Sort by score (descending), then by joinedAt (ascending)
    scores.sort((a, b) => {
      const scoreA = parseFloat(b.score);
      const scoreB = parseFloat(a.score);
      if (scoreA !== scoreB) {
        return scoreA - scoreB;
      }
      return a.joinedAt - b.joinedAt;
    });

    return scores;
  };

  // Calculate question statistics
  // Answers structure is: answers[questionIndex][participantId] = { selectedIndex/selectedIndices, ... }
  const calculateQuestionStats = () => {
    if (!instance || !instance.questions) return [];

    const questions = instance.questions;
    const stats = [];

    questions.forEach((question, questionIndex) => {
      const optionCounts = {};
      question.options?.forEach((_, optIndex) => {
        optionCounts[optIndex] = 0;
      });

      let totalAnswers = 0;
      let correctAnswers = 0;

      // Get all answers for this question
      const questionAnswers = answers[questionIndex] || {};
      
      Object.values(questionAnswers).forEach(answer => {
        if (answer !== undefined && answer !== null) {
          totalAnswers++;
          
          // Get selected indices (support both formats: single and multiple choice)
          let selectedIndices = [];
          if (answer.selectedIndices !== undefined) {
            // Multiple choice format
            selectedIndices = answer.selectedIndices;
          } else if (answer.selectedIndex !== undefined) {
            // Single choice format with object
            selectedIndices = answer.selectedIndex !== null ? [answer.selectedIndex] : [];
          } else if (typeof answer === 'number') {
            // Legacy format: just a number
            selectedIndices = [answer];
          }
          
          // Count each selected option
          selectedIndices.forEach(idx => {
            if (idx !== null && idx !== undefined) {
              optionCounts[idx] = (optionCounts[idx] || 0) + 1;
            }
          });
          
          // Get correct answer indices
          const correctIndices = question.correctAnswerIndices || 
            (question.correctAnswer !== undefined ? [question.correctAnswer] : [0]);
          
          // Check if correct (all correct must be selected and no incorrect)
          const isCorrect = selectedIndices.length === correctIndices.length &&
            correctIndices.every(idx => selectedIndices.includes(idx));
          
          if (isCorrect) {
            correctAnswers++;
          }
        }
      });

      const correctRate = totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0;
      const difficulty = correctRate >= 80 ? 'Leicht' : correctRate >= 50 ? 'Mittel' : 'Schwer';

      stats.push({
        questionIndex: questionIndex,
        question: question.question || question.text,
        correctAnswer: question.correctAnswerIndices || [question.correctAnswer],
        options: question.options || [],
        optionCounts,
        totalAnswers,
        correctAnswers,
        incorrectAnswers: totalAnswers - correctAnswers,
        correctRate: correctRate.toFixed(1),
        difficulty
      });
    });

    return stats;
  };

  const scores = calculateScores();
  const questionStats = calculateQuestionStats();

  if (loading) {
    return <LoadingScreen message="Lade Ergebnisse..." />;
  }

  if (error || !instance) {
    return (
      <ErrorScreen
        type="notFound"
        message={error || 'Quiz nicht gefunden'}
        backTo="/dashboard"
        backLabel="Zum Dashboard"
      />
    );
  }

  const participantCount = Object.keys(participants).length;
  
  // Calculate average - different logic for gamification vs percentage
  // If ANY score is gamification-based, show average as points, otherwise show as percentage
  const hasGamification = scores.some(s => s.isGamification);
  const averageValue = scores.length > 0
    ? (scores.reduce((sum, s) => sum + parseFloat(s.score), 0) / scores.length).toFixed(1)
    : 0;
  const averageDisplay = hasGamification 
    ? `Ø ${averageValue} Pkt.`
    : `Ø ${averageValue}%`;

  // Subtitle with instance info
  const subtitleElement = (
    <>Code: <span className="font-mono font-semibold">{instance.sessionCode}</span> • {participantCount} Teilnehmer • {averageDisplay}</>
  );

  return (
    <ViewLayout
      title={instance.name || 'Quiz-Ergebnisse'}
      subtitle={subtitleElement}
      onBack={() => navigate('/dashboard')}
      tabs={[
        { id: 'leaderboard', label: 'Rangliste' },
        { id: 'questions', label: 'Fragen-Statistik' },
        { id: 'export', label: 'Export' }
      ]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      primaryAction={instance.status === 'active' ? {
        label: 'Fortsetzen',
        onClick: () => navigate(`/live/${instanceId}`),
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      } : undefined}
      maxWidth="7xl"
    >
      {activeTab === 'leaderboard' && (
        <LeaderboardTab scores={scores} instance={instance} />
      )}
      {activeTab === 'questions' && (
        <QuestionStatsTab stats={questionStats} instance={instance} participantCount={participantCount} />
      )}
      {activeTab === 'export' && (
        <ExportTab
          instance={instance}
          scores={scores}
          questionStats={questionStats}
          participants={participants}
          answers={answers}
        />
      )}
    </ViewLayout>
  );
}

export default ResultsView;
